import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  listCheckins,
  getCheckin,
  toggleCheckinStatus,
  createCheckin,
  deleteCheckin,
  checkItem,
  quickCheckItem,
  undoCheckItem,
  addReceipt,
  closeShortfall,
  undoReceivingAction,
  undoReceivingHistory,
  undoReturnHistory,
  undoCheckHistory,
  undoLifecycleHistory,
  reopenShortfallItem,
  undoReturnAction,
  requestReturn,
  decideReturn,
  reopenReturnDecision,
  getSupplierBoxSummaries,
  updateBoxTransaction,
  listCheckinsPage,
  getCheckinAudit,
  getBoxMovements,
  undoBoxMovement,
} from "./api";
import type {
  CheckinPageQuery,
  EnrichedCheckin,
  DecideReturnPayload,
  PageResponse,
  RequestReturnPayload,
} from "./types";

const CHECKINS_KEY = ["checkins"];
export const CHECKIN_PAGES_KEY = ["checkins", "page"] as const;

function normalizeCheckinPageQuery(query: CheckinPageQuery): CheckinPageQuery {
  const normalized: CheckinPageQuery = {
    limit: query.limit,
    offset: query.offset,
    search: query.search.trim(),
  };
  const date = query.date?.trim();

  if (query.status) normalized.status = query.status;
  if (date) normalized.date = date;

  return normalized;
}

function replaceCheckinCache(
  qc: QueryClient,
  checkinId: string,
  updatedCheckin: EnrichedCheckin,
) {
  qc.setQueryData([...CHECKINS_KEY, checkinId], updatedCheckin);
  qc.setQueryData<PageResponse<EnrichedCheckin>>(CHECKINS_KEY, (current) => {
    if (!current?.items) return current;

    return {
      ...current,
      items: current.items.map((checkin) =>
        checkin.id === updatedCheckin.id ? updatedCheckin : checkin,
      ),
    };
  });
}

function invalidateCheckinSurfaces(qc: QueryClient, checkinId: string) {
  qc.invalidateQueries({ queryKey: [...CHECKINS_KEY, checkinId] });
  qc.invalidateQueries({ queryKey: CHECKINS_KEY });
  qc.invalidateQueries({ queryKey: ["orders"] });
  qc.invalidateQueries({ queryKey: ["dashboard"] });
}

function applyCheckinMutationSuccess(
  qc: QueryClient,
  checkinId: string,
  updatedCheckin: EnrichedCheckin,
) {
  replaceCheckinCache(qc, checkinId, updatedCheckin);
  invalidateCheckinSurfaces(qc, checkinId);
}

export function applyDecideReturnSuccess(
  queryClient: QueryClient,
  checkinId: string,
  updatedCheckin?: EnrichedCheckin,
) {
  if (updatedCheckin) {
    applyCheckinMutationSuccess(queryClient, checkinId, updatedCheckin);
  } else {
    invalidateCheckinSurfaces(queryClient, checkinId);
  }
  queryClient.invalidateQueries({ queryKey: ["alerts"] });
}

export function useCheckins() {
  return useQuery({
    queryKey: CHECKINS_KEY,
    queryFn: () => listCheckins(),
  });
}

export function useCheckinPage(query: CheckinPageQuery, enabled = true) {
  const normalizedQuery = normalizeCheckinPageQuery(query);

  return useQuery({
    queryKey: [...CHECKIN_PAGES_KEY, normalizedQuery],
    queryFn: ({ signal }) => listCheckinsPage(normalizedQuery, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useCheckin(id: string, freshOnMount = false) {
  return useQuery({
    queryKey: [...CHECKINS_KEY, id],
    queryFn: () => getCheckin(id),
    enabled: !!id,
    refetchOnMount: freshOnMount ? "always" : true,
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });
}

export function useCheckinAudit(checkinId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...CHECKINS_KEY, checkinId, "audit"],
    queryFn: () => getCheckinAudit(checkinId),
    enabled: !!checkinId && enabled,
  });
}

export function useCreateCheckin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderID: string) => createCheckin(orderID),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHECKINS_KEY }),
  });
}

export function useToggleCheckinStatus(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: "done" | "reopened") =>
      toggleCheckinStatus(checkinId, { action }),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
    },
  });
}

export function useUndoLifecycleHistory(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (auditId: number) => undoLifecycleHistory(checkinId, auditId),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
      qc.invalidateQueries({ queryKey: [...CHECKINS_KEY, checkinId, "audit"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}

export function useUndoCheckHistory(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, auditId }: { itemId: string; auditId: number }) =>
      undoCheckHistory(checkinId, itemId, auditId),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
      qc.invalidateQueries({ queryKey: [...CHECKINS_KEY, checkinId, "audit"] });
    },
  });
}

export function useDeleteCheckin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCheckin(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHECKINS_KEY }),
  });
}

export function useCheckItem(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      receivedQuantity,
    }: {
      itemId: string;
      receivedQuantity: number;
    }) => checkItem(checkinId, itemId, receivedQuantity),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
    },
  });
}

export function useQuickCheckItem(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => quickCheckItem(checkinId, itemId),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
    },
  });
}

export function useUndoCheckItem(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => undoCheckItem(checkinId, itemId),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
    },
  });
}

export function useAddReceipt(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, quantity }: { itemId: string; quantity: number }) =>
      addReceipt(checkinId, itemId, { quantity }),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
    },
  });
}

export function useCloseShortfall(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, note }: { itemId: string; note: string }) =>
      closeShortfall(checkinId, itemId, { note }),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
    },
  });
}

export function useUndoReceivingAction(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => undoReceivingAction(checkinId, itemId),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
    },
  });
}

export function useUndoReceivingHistory(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, auditId }: { itemId: string; auditId: number }) =>
      undoReceivingHistory(checkinId, itemId, auditId),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
      qc.invalidateQueries({ queryKey: [...CHECKINS_KEY, checkinId, "audit"] });
    },
  });
}

export function useReopenShortfallItem(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => reopenShortfallItem(checkinId, itemId),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
    },
  });
}

export function useUndoReturnAction(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => undoReturnAction(checkinId, itemId),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}

export function useUndoReturnHistory(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, auditId }: { itemId: string; auditId: number }) =>
      undoReturnHistory(checkinId, itemId, auditId),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
      qc.invalidateQueries({ queryKey: [...CHECKINS_KEY, checkinId, "audit"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}

export function useRequestReturn(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      quantity,
      note,
    }: { itemId: string } & RequestReturnPayload) =>
      requestReturn(checkinId, itemId, { quantity, note }),
    onSuccess: (updatedCheckin) => {
      applyCheckinMutationSuccess(qc, checkinId, updatedCheckin);
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}

export function useDecideReturn(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      ...payload
    }: { itemId: string } & DecideReturnPayload) =>
      decideReturn(checkinId, itemId, payload),
    onSuccess: (updatedCheckin) => {
      applyDecideReturnSuccess(qc, checkinId, updatedCheckin);
    },
  });
}

export function useReopenReturnDecision(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => reopenReturnDecision(checkinId, itemId),
    onSuccess: (updatedCheckin) => {
      applyDecideReturnSuccess(qc, checkinId, updatedCheckin);
    },
  });
}

export function useSupplierBoxSummaries(checkinId: string, enabled = true) {
  return useQuery({
    queryKey: [...CHECKINS_KEY, checkinId, "suppliers"],
    queryFn: () => getSupplierBoxSummaries(checkinId),
    enabled: !!checkinId && enabled,
    refetchOnWindowFocus: true,
  });
}

export function useBoxMovements(
  checkinId: string,
  supplierId: string,
  offset: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [
      ...CHECKINS_KEY,
      checkinId,
      "suppliers",
      supplierId,
      "box-movements",
      offset,
    ],
    queryFn: () => getBoxMovements(checkinId, supplierId, offset),
    enabled,
    retry: false,
  });
}

export function useUndoBoxMovement(checkinId: string, supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (movementId: string) =>
      undoBoxMovement(checkinId, supplierId, movementId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...CHECKINS_KEY, checkinId, "suppliers"],
      });
      qc.invalidateQueries({ queryKey: [...CHECKINS_KEY, checkinId] });
    },
  });
}

export function useUpdateBoxTransaction(checkinId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      supplierID,
      boxesSent,
      boxesReceived,
    }: {
      supplierID: string;
      boxesSent: number;
      boxesReceived: number;
    }) => updateBoxTransaction(checkinId, supplierID, boxesSent, boxesReceived),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...CHECKINS_KEY, checkinId, "suppliers"],
      });
      qc.invalidateQueries({ queryKey: [...CHECKINS_KEY, checkinId] });
    },
  });
}
